import { useState, useEffect } from 'react';
import { useFollowupNotifications, type InAppNotification } from '../hooks/useFollowupNotifications';

interface NotificationCenterProps {
  onNotificationClick?: (leadId: string) => void;
}

export default function NotificationCenter({ onNotificationClick }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const { getNotifications, markAsRead, clearAll, subscribe } = useFollowupNotifications(true);

  useEffect(() => {
    // Subscribe to notification updates
    const unsubscribe = subscribe((updatedNotifications) => {
      setNotifications(updatedNotifications);
    });

    // Load initial notifications
    setNotifications(getNotifications());

    return unsubscribe;
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: InAppNotification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    
    // If this is a follow-up notification with a lead ID, navigate to the lead
    if (notification.type === 'followup' && notification.leadId && onNotificationClick) {
      onNotificationClick(notification.leadId);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-slate-700/60 transition-colors"
      >
        <span className="text-xl">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl z-50 max-h-96 overflow-hidden flex flex-col">
            <div className="p-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Notifications</h3>
              <button
                onClick={() => {
                  clearAll();
                  setIsOpen(false);
                }}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Clear All
              </button>
            </div>
            
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                No notifications yet
              </div>
            ) : (
              <div className="overflow-y-auto flex-1">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">
                        {notification.type === 'followup' ? '📅' : '🔔'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-slate-800">
                          {notification.title}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 line-clamp-2">
                          {notification.message}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1">
                          {formatTime(notification.timestamp)}
                        </div>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
